import type { RouteNamedMap } from 'vue-router/auto-routes';

type RoutesList = keyof RouteNamedMap;

const ticketRoutes = {
  NEW_TICKET: 'ticket-new',
  TICKET_HISTORY: 'ticket-history',
  TICKET_CONTRACT_MANAGEMENT: 'ticket-contract-management',
  TICKET_ATTENDEE_CONTRACT_PREVIEW: 'ticket-attendees-contract-id-preview',
  TICKET_DEPOSIT_MANAGEMENT: 'ticket-deposit-management',
  TICKET_DEPOSIT_MANAGEMENT_INFOS: 'ticket-deposit-id-infos',
  TICKET_ADVANTAGES_ACTUAL: 'ticket-advantages-actual',
  TICKET_ADVANTAGES_PLANNED: 'ticket-advantages-planned',
  TICKET_ATTENDEES: 'ticket-attendees',
  TICKET_GENERAL_INFORMATION: 'ticket-general-information',
  TICKET_INFO: 'ticket-infos',
  TICKET_INFO_ATTENDEE: 'ticket-attendees-id-infos',

  TICKET_DEPOSIT_INFO: 'ticket-deposit-infos',

  TICKET_SIGNING: 'ticket-attendees-signing',
  TICKET_SIGNING_SHEET: 'ticket-signing-sheet',
} as const satisfies Record<string, RoutesList>;

const adminRoutes = {
  ADMIN: 'admin_index',
  ADMIN_IDENTITY: 'admin_identity',

  ADMIN_CONTACT: 'admin_contact',
  ADMIN_CONTACT_EDIT: 'admin_contact_edit',
  ADMIN_CONTACT_CREATE: 'admin_contact_create',
  ADMIN_CONTACTS: 'admin_contacts',

  ADMIN_CONTRACT_TEMPLATES: 'admin_contract_templates',
  ADMIN_CONTRACT_TEMPLATES_CREATE: 'admin_contract_templates_create',
  ADMIN_CONTRACT_TEMPLATES_EDIT: 'admin_contract_templates_edit',

  ADMIN_ATTACHMENT_TEMPLATE_CREATE: 'admin_attachment_template_create',
  ADMIN_ATTACHMENTS_TEMPLATES: 'admin_attachments_templates',

  ADMIN_CRM_USERS: 'admin_crm_users',
  ADMIN_CRM_USER_CREATE: 'admin_crm_user_create',
  ADMIN_CRM_USER_EDIT: 'admin_crm_user_edit',

  ADMIN_CONVENTIONS: 'admin_conventions',
  ADMIN_CONVENTION_CREATE: 'admin_convention_create',
  ADMIN_CONVENTION_EDIT: 'admin_convention_edit',

  ADMIN_DECLARATIONS: 'admin_declarations',
  ADMIN_DECLARATION_CREATE: 'admin_declaration_create',
  ADMIN_DECLARATION_EDIT: '/admin/declaration/[code]/edit/',
  ADMIN_DECLARATION_EDIT_THEMES: '/admin/declaration/[code]/edit/themes',
  ADMIN_DECLARATION_EDIT_ATTACHMENTS:
    '/admin/declaration/[code]/edit/attachments',
  ADMIN_DECLARATION_EDIT_DEPOSIT: '/admin/declaration/[code]/edit/deposit',
  ADMIN_DECLARATION_EDIT_SIMPLE_EVENTS:
    '/admin/declaration/[code]/edit/simple-events',

  ADMIN_INTERNAL_SIGNER_EDIT: 'admin_internal_signer_edit',
  ADMIN_INTERNAL_SIGNER_CREATE: 'admin_internal_signer_create',
  ADMIN_INTERNAL_SIGNERS: 'admin_internal_signers',

  ADMIN_OPERATION_EDIT: 'admin_operation_edit',
  ADMIN_OPERATION_CREATE: 'admin_operation_create',
  ADMIN_OPERATIONS: 'admin_operations',

  ADMIN_PACKAGE: 'admin_packages',
  ADMIN_ACTUAL_PACKAGE_EDIT: 'admin_actual_package_edit',
  ADMIN_PLANNED_PACKAGE_EDIT: 'admin_planned_package_edit',
  ADMIN_PACKAGE_CREATE: 'admin_package_create',
  ADMIN_PACKAGES: 'admin_packages',

  ADMIN_TICKETS: 'admin_tickets',

  ADMIN_USER: 'admin_user',
  ADMIN_USER_EDIT: 'admin_user_edit',
  ADMIN_USER_CREATE: 'admin_user_create',
  ADMIN_USERS: 'admin_users',

  ADMIN_WORKFLOW_EDIT: 'admin_workflow_edit',
  ADMIN_WORKFLOW_CREATE: 'admin_workflow_create',
  ADMIN_WORKFLOWS: 'admin_workflows',

  ADMIN_SETTINGS_UPSELL: 'admin_settings_upsell',
  ADMIN_SETTINGS_SIGNATURES: 'admin_settings_signatures',
  ADMIN_SETTINGS_WATCHDOGS: 'admin_settings_watchdogs',
  ADMIN_SETTINGS_PERMISSIONS: 'admin_settings_permissions',
  ADMIN_SETTINGS_ATTENDEE_SOURCES: 'admin_settings_attendee_sources',

  ADMIN_SETTINGS_NOTIFICATIONS: 'admin_settings_notifications',
  ADMIN_SETTINGS_NOTIFICATIONS_EDIT: 'admin_settings_notification_edit',
  ADMIN_SETTINGS_NOTIFICATIONS_CREATE: 'admin_settings_notification_create',

  ADMIN_SPECIALTY_CREATE: 'admin_specialty_create',
  ADMIN_SPECIALTY_EDIT: 'admin_specialty_edit',
  ADMIN_SPECIALTIES: 'admin_specialties',

  ADMIN_ALLOCATION_UNIT: 'admin_units',
  ADMIN_UNIT_CREATE: 'admin_unit_create',
  ADMIN_UNIT_EDIT: 'admin_unit_edit',

  ADMIN_ALLOCATION_NETWORK: 'admin_networks',
  ADMIN_NETWORK_CREATE: 'admin_network_create',
  ADMIN_NETWORK_EDIT: 'admin_network_edit',

  ADMIN_ALLOCATION_PRODUCT: 'admin_products',
  ADMIN_PRODUCT_CREATE: 'admin_product_create',
  ADMIN_PRODUCT_EDIT: 'admin_product_edit',

  ADMIN_ALLOCATION_REGION: 'admin_regions',
  ADMIN_REGION_CREATE: 'admin_region_create',
  ADMIN_REGION_EDIT: 'admin_region_edit',

  ADMIN_ALLOCATION_SECTOR: 'admin_sectors',
  ADMIN_SECTOR_CREATE: 'admin_sector_create',
  ADMIN_SECTOR_EDIT: 'admin_sector_edit',

  ADMIN_ALLOCATION_THERAPEUTIC_AREA: 'admin_therapeutic_areas',
  ADMIN_THERAPEUTIC_AREA_CREATE: 'admin_therapeutic_area_create',
  ADMIN_THERAPEUTIC_AREA_EDIT: 'admin_therapeutic_area_edit',

  ADMIN_ALLOCATION_UGA: 'admin_ugas',
  ADMIN_UGA_CREATE: 'admin_uga_create',
  ADMIN_UGA_EDIT: 'admin_uga_edit',

  ADMIN_ALLOCATION_ZONE: 'admin_zones',
  ADMIN_ZONE_CREATE: 'admin_zone_create',
  ADMIN_ZONE_EDIT: 'admin_zone_edit',

  ADMIN_ALLOCATION_IDENTITY: 'admin_identities',
  ADMIN_IDENTITY_CREATE: 'admin_identity_create',
  ADMIN_IDENTITY_EDIT: 'admin_identity_edit',
} as const satisfies Record<string, RoutesList>;

const depositRoutes = {
  DEPOSIT_LIST: 'deposit-list',
} as const satisfies Record<string, RoutesList>;

const rootRoutes = {
  DASHBOARD: 'dashboard',
  LIBRARY: 'library',
  TRANSPARENCY: 'transparency',
  ANALYTICS: 'analytics',
  PROFILE: 'profile',
  NOT_FOUND: 'not-found',
  TECHNICAL_ERROR: '500',
  UNAUTHORIZED: 'unauthorized',
} as const satisfies Record<string, RoutesList>;

const routeTicketEnum = {
  ...adminRoutes,
  ...ticketRoutes,
  ...depositRoutes,
  ...rootRoutes,
} as const;

export {
  routeTicketEnum,
  adminRoutes,
  ticketRoutes,
  depositRoutes,
  rootRoutes,
};

export type { RoutesList };
